import type { CbcPackage, FieldFileRef } from './cbcTranslation';

const TERMINAL = new Set(['completed', 'failed', 'manual_intervention_required', 'awaiting_applicant_translation']);
const REPLACEMENT_REQUESTED = 'replacement_requested';

export const CBC_PROCESSING_TIMEOUT_MS = (() => {
  const raw = Number(import.meta.env.VITE_CBC_PROCESSING_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : 10 * 60 * 1000;
})();

export const CBC_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;

export const CBC_PROCESSING_LOCK_MESSAGE =
  "We're processing your background check document — this usually takes a few minutes. " +
  'You can keep working on other steps; uploading a replacement becomes available again shortly.';

export const CBC_PROCESSING_DELAYED_NOTICE =
  "We've received your background check document, but processing is taking longer than expected. " +
  'You can wait while we continue processing, contact our team for help, or upload a replacement document. ' +
  'You do not need to provide an English translation.';

export const CBC_AUTO_SUBMIT_FAILED_NOTICE =
  "Your background check was uploaded, but we couldn't start processing it automatically. " +
  'Your file is still attached — please press Save & Continue to start processing.';

export interface CbcUploadLock {
  fieldKey: string;
  message: string;
}

export function isCbcDocumentType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return /police[\s_-]*clearance|criminal[\s_-]*(background|record)|background[\s_-]*check/i.test(documentType);
}

/** Find CBC advanced-upload fields across current and legacy served configs. */
export function findCbcFieldKeys(config: unknown): string[] {
  const found = new Set<string>();
  const seen = new Set<object>();
  const walk = (value: unknown) => {
    if (!value || typeof value !== 'object' || seen.has(value as object)) return;
    seen.add(value as object);
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    const node = value as Record<string, unknown>;
    const fieldKey =
      (typeof node.name === 'string' && node.name) ||
      (typeof node.id === 'string' && node.id) ||
      undefined;
    const metadata =
      node.metadata && typeof node.metadata === 'object'
        ? (node.metadata as Record<string, unknown>)
        : undefined;
    const candidates = [metadata?.documentType, node.documentType, node.displayName, fieldKey];
    if (
      fieldKey &&
      candidates.some((candidate) =>
        isCbcDocumentType(typeof candidate === 'string' ? candidate : undefined),
      )
    ) {
      found.add(fieldKey);
    }
    Object.values(node).forEach(walk);
  };
  walk(config);
  return [...found];
}

/** Stable identity for the first persisted file in an engine upload field. */
export function uploadedDocumentIdentity(value: unknown): string | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  if (!first) return undefined;
  if (typeof first === 'string') return `url:${first}`;
  if (typeof first !== 'object') return undefined;
  const ref = first as Record<string, unknown>;
  const id =
    (typeof ref.documentId === 'string' && ref.documentId) ||
    (typeof ref.id === 'string' && ref.id) ||
    undefined;
  if (id) return `id:${id}`;
  const url =
    (typeof ref.downloadUrl === 'string' && ref.downloadUrl) ||
    (typeof ref.url === 'string' && ref.url) ||
    undefined;
  return url ? `url:${url}` : undefined;
}

export function cbcPackageFingerprint(pkg: CbcPackage | null | undefined): string | null {
  if (!pkg?.record) return null;
  return [
    pkg.record.recordId,
    String(pkg.record.versionNumber),
    pkg.record.original?.artifactId ?? '',
  ].join('|');
}

/** Unknown baselines fail closed; timestamps are never treated as identity. */
export function isCbcPackageNewerThanBaseline(
  pkg: CbcPackage | null | undefined,
  baselineFingerprint: string | null | undefined,
): boolean {
  const fingerprint = cbcPackageFingerprint(pkg);
  if (!fingerprint || baselineFingerprint === undefined) return false;
  return fingerprint !== baselineFingerprint;
}

const hasCurrentFile = (
  pkg: CbcPackage,
  getFieldFile: (fieldKey: string) => FieldFileRef | undefined,
) => {
  const field = getFieldFile(pkg.fieldKey);
  const recordName = pkg.record?.original?.fileName;
  return !(recordName && field?.fileName && recordName !== field.fileName);
};

export function deriveCbcUploadLocks(
  packages: CbcPackage[],
  stepKey: string,
  getFieldFile: (fieldKey: string) => FieldFileRef | undefined,
  now: number = Date.now(),
): CbcUploadLock[] {
  const locks: CbcUploadLock[] = [];
  for (const pkg of packages) {
    if (pkg.stepKey !== stepKey || !pkg.record) continue;
    const rec = pkg.record;
    if (
      TERMINAL.has(rec.processingStatus) ||
      rec.processingStatus === 'not_started' ||
      pkg.status === REPLACEMENT_REQUESTED
    ) {
      continue;
    }
    const updatedAt = Date.parse(rec.updatedAt);
    if (!Number.isFinite(updatedAt) || updatedAt - now > CBC_MAX_FUTURE_SKEW_MS) continue;
    if (now - updatedAt >= CBC_PROCESSING_TIMEOUT_MS || !hasCurrentFile(pkg, getFieldFile)) continue;
    locks.push({ fieldKey: pkg.fieldKey, message: CBC_PROCESSING_LOCK_MESSAGE });
  }
  return locks;
}

export function deriveCbcProcessingDelays(
  packages: CbcPackage[],
  stepKey: string,
  getFieldFile: (fieldKey: string) => FieldFileRef | undefined,
  now: number = Date.now(),
): string[] {
  const delayed = new Set<string>();
  for (const pkg of packages) {
    if (pkg.stepKey !== stepKey || !pkg.record) continue;
    const rec = pkg.record;
    if (
      TERMINAL.has(rec.processingStatus) ||
      rec.processingStatus === 'not_started' ||
      pkg.status === REPLACEMENT_REQUESTED
    ) {
      continue;
    }
    const updatedAt = Date.parse(rec.updatedAt);
    if (
      !Number.isFinite(updatedAt) ||
      updatedAt - now > CBC_MAX_FUTURE_SKEW_MS ||
      now - updatedAt < CBC_PROCESSING_TIMEOUT_MS ||
      !hasCurrentFile(pkg, getFieldFile)
    ) {
      continue;
    }
    delayed.add(pkg.fieldKey);
  }
  return [...delayed];
}

/** Earliest trustworthy deadline at which a server lock must be recomputed. */
export function nextCbcUploadLockExpiry(
  packages: CbcPackage[],
  stepKey: string,
  now: number = Date.now(),
): number | null {
  let earliest: number | null = null;
  for (const pkg of packages) {
    if (pkg.stepKey !== stepKey || !pkg.record) continue;
    const rec = pkg.record;
    if (
      TERMINAL.has(rec.processingStatus) ||
      rec.processingStatus === 'not_started' ||
      pkg.status === REPLACEMENT_REQUESTED
    ) {
      continue;
    }
    const updatedAt = Date.parse(rec.updatedAt);
    if (!Number.isFinite(updatedAt) || updatedAt - now > CBC_MAX_FUTURE_SKEW_MS) continue;
    const deadline = updatedAt + CBC_PROCESSING_TIMEOUT_MS;
    if (deadline <= now) continue;
    earliest = earliest == null ? deadline : Math.min(earliest, deadline);
  }
  return earliest;
}